import { useCompany, getCurrentCompany } from '@core/utils/hooks/misc';
import { ColumnGroupType, FieldMapping, FieldMappingType, ReportDataType } from '@business/entities/dynamicReprots';
import { DateFilterByEnum, LooseObject, MapSchema, ReportResponse, ReportStatusEnum } from '@business/entities/report';
import { Formatter } from '@core/utils/formatter';
import { R } from '@core/utils/r';
import moment from 'moment-timezone';
import { ReportService } from '.';
import { durationHumanizeReports } from '@core/utils/time';
import { CommonService } from '../common_service';
export function reportColumnResponse(report: ReportResponse) {
  const reportSchema: LooseObject = {};

  R.forEach(report.fields, field => {
    reportSchema[field.name] = field.type;
  });
  type ReportFields = MapSchema<typeof reportSchema>;
}

export function getDateFilterTitle(filterBy: DateFilterByEnum) {
  switch (filterBy) {
    case DateFilterByEnum.createdAt:
      return 'Created On';
    case DateFilterByEnum.lastActivity:
      return 'Last Activity';
    case DateFilterByEnum.completedAt:
      return 'Completed On';
    case DateFilterByEnum.openedAt:
      return 'Opened On';
  }
  return filterBy;
}
export function getDateFilters() {
  return [DateFilterByEnum.createdAt, DateFilterByEnum.lastActivity];
}

export function getTicketDateFilters() {
  return [DateFilterByEnum.createdAt, DateFilterByEnum.openedAt, DateFilterByEnum.completedAt];
}

export function getStatusFilterTitle(filterBy: ReportStatusEnum) {
  switch (filterBy) {
    case ReportStatusEnum.completed:
      return 'Completed';
    case ReportStatusEnum.opened:
      return 'Opened';
    case ReportStatusEnum.pending:
      return 'Pending';
    case ReportStatusEnum.active:
      return 'Active';
    case ReportStatusEnum.overdue:
      return 'Overdue';
    case ReportStatusEnum.delivered:
      return 'Delivered';
    case ReportStatusEnum.all:
      return 'All';
    case ReportStatusEnum.no:
      return 'No';
    case ReportStatusEnum.yes:
      return 'Yes';
  }
  return filterBy;
}

export function getTicketStatusFilters() {
  return [ReportStatusEnum.all, ReportStatusEnum.opened, ReportStatusEnum.completed];
}

export function getMbStatuses() {
  return [ReportStatusEnum.pending, ReportStatusEnum.active, ReportStatusEnum.overdue, ReportStatusEnum.delivered];
}

export function getNewSaleStatuses() {
  return [ReportStatusEnum.all, ReportStatusEnum.yes, ReportStatusEnum.no];
}

/* Builder Stuff */
export function getGroupByOptions(countOnly = false): { label: string; value: ColumnGroupType }[] {
  if (countOnly) {
    return [
      {
        label: 'Count',
        value: ColumnGroupType.count,
      },
    ];
  }
  return [
    {
      label: 'None',
      value: ColumnGroupType.none,
    },
    {
      label: 'Sum',
      value: ColumnGroupType.sum,
    },
    {
      label: 'Min',
      value: ColumnGroupType.min,
    },
    {
      label: 'Max',
      value: ColumnGroupType.max,
    },
    {
      label: 'Comma Seperated',
      value: ColumnGroupType.comma,
    },
    {
      label: 'Average',
      value: ColumnGroupType.avg,
    },
    {
      label: 'Count',
      value: ColumnGroupType.count,
    },
  ];
}

export function getColumnGroupBy(columnName: string, type: FieldMappingType, groupBy?: string): ColumnGroupType {
  if (groupBy) {
    if (groupBy === columnName) {
      return ColumnGroupType.none;
    }
    if (type === FieldMappingType.number || type === FieldMappingType.currency) {
      return ColumnGroupType.sum;
    }
    if (type == FieldMappingType.date) {
      return ColumnGroupType.none;
    }
    return ColumnGroupType.comma;
  }

  return ColumnGroupType.none;
}

export function getFilterOperators(mapping?: FieldMapping) {
  if (!mapping) return [];

  if (!!R.keys(mapping.filterItems).length) {
    return [
      {
        label: 'In',
        value: 'in',
      },
      {
        label: 'Not In',
        value: 'not in',
      },
    ];
  }
  const operators = ReportService.operators(mapping);
  return [
    ...operators,
    { label: 'Contains', value: 'like' },
    { label: 'Does not Contains', value: 'not like' },
    { label: 'Empty', value: 'empty' },
    { label: 'Not Empty', value: 'not empty' },
  ];
}

export function operators(mapping: FieldMapping) {
  if (mapping.type === FieldMappingType.number || mapping.type === FieldMappingType.currency) {
    return [
      {
        label: 'Equals',
        value: '=',
      },
      {
        label: 'Not Equals',
        value: '!=',
      },
      {
        label: 'Greater Than',
        value: '>',
      },
      {
        label: 'Greater Than Or Equal To',
        value: '>=',
      },
      {
        label: 'Less Than',
        value: '<',
      },
      {
        label: 'Less Than Or Equal To',
        value: '=<',
      },
    ];
  } else {
    return [
      {
        label: 'Equals',
        value: '=',
      },
      {
        label: 'Not Equals',
        value: '!=',
      },
    ];
  }
}

export function renderReportCell(row: ReportDataType, key: string, mapping: FieldMapping, raw = false) {
  const value = row[key];

  if (!mapping || raw) return value;
  if (mapping.type === FieldMappingType.boolean) {
    if (R.isNumber(value)) {
      return R.toNumber(value) ? 'Yes' : 'No';
    }
    return value;
  } else if (mapping.type === FieldMappingType.date) {
    if (R.isEmpty(value)) return '';
    return moment(value as string, 'YYYY-MM-DD').format('MM-DD-YYYY');
  } else if (mapping.type === FieldMappingType.number) {
    const num = R.toNumber(value);
    if (mapping.duration == 's') {
      return durationHumanizeReports(num);
    } else if (mapping.duration == 'm') {
      return durationHumanizeReports(num * 60);
    } else if (mapping.duration == 'h') {
      return durationHumanizeReports(num * 3600);
    }
    return num.toFixed(0);
  } else if (mapping.type === FieldMappingType.currency) {
    return Formatter.formatCurrency(R.toNumber(value), getCurrentCompany().currencyCode);
  } else if (mapping.isArray) {
    try {
      const arrayValues = R.flatten(
        R.map(R.flatten(JSON.parse(R.toString(value)) as string[]), i => {
          try {
            return JSON.parse(R.toString(i));
          } catch (e) {}
          return [i];
        }),
      );

      if (arrayValues.length > 0) {
        return R.compact(R.uniq(arrayValues)).join(', ');
      }
    } catch (ex) {}
    return R.replace(R.toString(value), '[', '').replace(']', '');
  } else if (mapping.key === 'custom') {
    if (R.isNumber(value)) {
      return Formatter.formatDecimal(R.toNumber(value), 2, 2);
    }
  }

  if (R.isString(value)) {
    try {
      const result = JSON.parse(value);
      if (R.isArray(result)) {
        return result.join(', ');
      }
      return value;
    } catch (e) {
      return value;
    }
  }

  return R.toString(value);
}

export function isYesaReport(name: string) {
  if (!CommonService.isYesa()) return false;
  const reports = ['sales-activity-report', 'yesa-training-report', 'commissions-report-by-date-range', 'manager-report', 'manager-report-weekly'];
  return reports.includes(name);
}
export function isDownloadableReportForYesa(name: string) {
  if (!CommonService.isYesa()) return false;
  const reports = ['manager-report', 'manager-report-weekly'];
  return reports.includes(name);
}
